import { Box, Button, Tag, Text, Title } from '@factorialco/deprecated-design-system'
import { FinancialDocumentExpenseFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import tagColor from 'modules/expenses/lib/tagColors'

interface Props {
  expense: FinancialDocumentExpenseFragment
}

const ExpenseDetails = ({ expense }: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  if (!expense) return null

  const currency = expense.currency

  return (
    <Box paddingTop="s16" gap="s24">
      <Box flexDirection="row" gap="s4">
        <Title variant="600" title={i18n.t('finance.invoices.detail.expense_detail.title')} />
        {expense.expensable.id && (
          <Button.Router
            aria-label={i18n.t('finance.invoices.detail.fields.router_label_purchase_order')}
            type="secondary"
            blankTarget
            to={navigation.finance.spending.expenses.list.expenses.expenseId.fullPath({
              expense_id: expense.expensable.id,
            })}
            iconName="arrow-right"
          />
        )}
      </Box>

      <Box flexDirection="row" gap="s4">
        <Box minWidth="s240" flexNoShrink textAlign="left" gap="s4">
          <Text weight="normal" size="300" color="grey800">
            {i18n.t('finance.invoices.detail.expense_detail.fields.status')}
          </Text>
          <Tag showCircle type={tagColor(expense.status)} leftItem="circle" inline>
            {getStatusLabel(expense.status)}
          </Tag>
        </Box>
      </Box>

      <Box flexDirection="row" gap="s4">
        <Box minWidth="s240" flexNoShrink textAlign="left" gap="s4">
          <Text weight="normal" size="300" color="grey800">
            {i18n.t('finance.invoices.detail.expense_detail.fields.document_type')}
          </Text>
          <Text weight="medium" size="300" color="grey1000">
            {expense.documentType ?? ''}
          </Text>
        </Box>

        <Box minWidth="s240" flexNoShrink textAlign="left" gap="s4">
          <Text weight="normal" size="300" color="grey800">
            {i18n.t('finance.invoices.detail.expense_detail.fields.report_date')}
          </Text>
          <Text weight="medium" size="300" color="grey1000">
            {dateBackendDateTimeToUI(expense.reviewRequestAt || expense.effectiveOn)}
          </Text>
        </Box>
      </Box>

      {expense.taxes.map((tax, index) => (
        <>
          <Box flexDirection="row" gap="s4">
            <Box minWidth="s240" flexNoShrink textAlign="left" gap="s4">
              <Text weight="normal" size="300" color="grey800">
                {i18n.t('finance.invoices.detail.expense_detail.fields.taxes.base_amount')}
              </Text>
              <Text weight="medium" size="300" color="grey1000">
                {formattedAmount({
                  amountCents: tax.baseAmount || 0,
                  currency,
                  locale,
                })}
              </Text>
            </Box>
            <Box minWidth="s240" flexNoShrink textAlign="left" gap="s4">
              <Text weight="normal" size="300" color="grey800">
                {i18n.t('finance.invoices.detail.expense_detail.fields.taxes.vat_amount')}
              </Text>
              <Text weight="medium" size="300" color="grey1000">
                {formattedAmount({
                  amountCents: tax.amount || 0,
                  currency,
                  locale,
                })}
              </Text>
            </Box>
          </Box>

          <Box flexDirection="row" gap="s4">
            <Box minWidth="s240" flexNoShrink textAlign="left" gap="s4">
              <Text weight="normal" size="300" color="grey800">
                {i18n.t('finance.invoices.detail.expense_detail.fields.taxes.vat_type')}
              </Text>
              <Text weight="medium" size="300" color="grey1000">
                {((tax.percentage || 0) * 100).toFixed(2) + '%'}
              </Text>
            </Box>
          </Box>
        </>
      ))}
    </Box>
  )
}
export default ExpenseDetails
